import type { Product, RoutineStep, SkinType } from '../../core/types/catalog'

export const sorts = ['Aanbevolen', 'Prijs oplopend', 'Prijs aflopend'] as const
export type Sort = (typeof sorts)[number]
export const DEFAULT_SORT: Sort = 'Aanbevolen'

/** Met ruim veertig producten: eerst een rustige eerste indruk, de rest op verzoek. */
export const PAGE_SIZE = 12
/** Op deze plek in de grid staat de adviestegel (alleen als er meer producten zijn). */
export const ADVICE_SLOT = 3
/** Zoveel protocollen onder de collectie. */
export const ROUTINES_SHOWN = 3

/** Producten met een van deze werkzame stoffen (stukjes tekst, hoofdletters maken niet uit); bestsellers eerst. Puur. */
export function productsWithIngredient(products: readonly Product[], match: readonly string[]): Product[] {
  const needles = match.map((m) => m.trim().toLowerCase()).filter(Boolean)
  if (needles.length === 0) return []
  const hits = products.filter((p) => p.keyIngredients.some((k) => needles.some((m) => k.toLowerCase().includes(m))))
  return [...hits].sort((a, b) => Number(Boolean(b.bestseller)) - Number(Boolean(a.bestseller)))
}

/** Namen van de filters in de URL: /webshop?huid=&merk=&stap=&sorteer=&zoek= */
export const PARAM = { skin: 'huid', brand: 'merk', step: 'stap', sort: 'sorteer', query: 'zoek' } as const

export type FilterState = {
  skin: SkinType | null
  /** `null` = alle merken. */
  brand: string | null
  step: RoutineStep | null
  sort: Sort
  query: string
}

/** Zoeken zonder hoofdletters en accenten: "creme" vindt "crème", "serum" vindt "Sérum". */
export const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

const searchText = (p: Product) => normalize([p.name, p.brand, p.note, p.step, ...p.keyIngredients, ...p.skinTypes].join(' '))

/** Salonfavorieten eerst, dan bestsellers. */
const recommendedScore = (p: Product) => Number(Boolean(p.salonFavourite)) * 2 + Number(Boolean(p.bestseller))

/** Filteren en sorteren in één pure stap. */
export function visibleProducts(products: Product[], f: FilterState): Product[] {
  const q = normalize(f.query.trim())
  const filtered = products.filter(
    (p) =>
      (f.brand === null || p.brand === f.brand) &&
      (!f.skin || p.skinTypes.includes(f.skin)) &&
      (!f.step || p.step === f.step) &&
      (q === '' || searchText(p).includes(q)),
  )
  if (f.sort === 'Prijs oplopend') return [...filtered].sort((a, b) => a.price - b.price)
  if (f.sort === 'Prijs aflopend') return [...filtered].sort((a, b) => b.price - a.price)
  return [...filtered].sort((a, b) => recommendedScore(b) - recommendedScore(a))
}

/** Filters uit de URL lezen. Onbekende waarden worden genegeerd. */
export function filtersFromParams(params: URLSearchParams, known: { skins: readonly SkinType[]; brands: string[]; products: Product[] }): FilterState {
  const skinParam = params.get(PARAM.skin)
  const brandParam = params.get(PARAM.brand)
  const stepParam = params.get(PARAM.step)
  const sortParam = params.get(PARAM.sort)
  return {
    skin: known.skins.find((s) => s === skinParam) ?? null,
    brand: brandParam && known.brands.includes(brandParam) ? brandParam : null,
    step: known.products.find((p) => p.step !== undefined && p.step === stepParam)?.step ?? null,
    sort: sorts.find((s) => s === sortParam) ?? DEFAULT_SORT,
    query: params.get(PARAM.query) ?? '',
  }
}

/** Filters naar de URL: alleen wat afwijkt van de standaard. */
export function paramsFromFilters(f: FilterState): URLSearchParams {
  const next = new URLSearchParams()
  if (f.skin) next.set(PARAM.skin, f.skin)
  if (f.brand) next.set(PARAM.brand, f.brand)
  if (f.step) next.set(PARAM.step, f.step)
  if (f.sort !== DEFAULT_SORT) next.set(PARAM.sort, f.sort)
  if (f.query.trim()) next.set(PARAM.query, f.query.trim())
  return next
}
